import * as React from 'react';
import {
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import { useIntl } from 'react-intl';
import { CustomSetting, DropdownOption, Setting } from 'types/types';

const useStyles = makeStyles((theme) => ({
  fieldContainer: {
    display: 'flex',
    flex: 1,
    marginTop: theme.spacing(1),
    flexDirection: 'row',
    gap: theme.spacing(1.25)
  },
  inputText: { fontSize: 13 },
  formControl: {
    flex: 1
  },
  conditionFormControl: {
    flex: 0.7
  },
  valueFormControl: {
    flex: 1
  }
}));

type FieldValidation = {
  field: boolean;
  condition: boolean;
  value: boolean;
};

type ChangedFieldsMap = {
  [settingId: string]: FieldValidation[];
};

type FilterFieldSectionProps = {
  index: number;
  isInvalidField: FieldValidation;
  changedFields: ChangedFieldsMap;
  setChangedFields: React.Dispatch<React.SetStateAction<ChangedFieldsMap>>;
  initialSelectValue: {
    key: string;
    value: string | null;
    field?: string | null;
    condition?: string | null;
  };
  setSettings: React.Dispatch<React.SetStateAction<Setting[]>>;
  inputSetting: CustomSetting;
};

export const FilterFieldSection: React.FC<FilterFieldSectionProps> = ({
  index,
  isInvalidField,
  changedFields,
  setChangedFields,
  initialSelectValue,
  setSettings,
  inputSetting
}: FilterFieldSectionProps) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const settingId = inputSetting?.id;
  const fieldOptions = inputSetting?.fieldOptions as DropdownOption[];
  const conditionOptions = inputSetting?.conditionOptions as DropdownOption[];

  const isFieldChanged = (settingId && changedFields[settingId]?.[index]) || {
    field: false,
    condition: false,
    value: false
  };
  const invalidField = isInvalidField || {
    field: false,
    condition: false,
    value: false
  };

  const getDefaultCondition = () => {
    if (initialSelectValue.condition) return initialSelectValue.condition;
    if (conditionOptions && conditionOptions.length === 1) {
      return Object.values(conditionOptions[0])[0] as string;
    }
    return null;
  };

  const [filterField, setFilterField] = React.useState({
    field: initialSelectValue.field || null,
    condition: getDefaultCondition(),
    value: initialSelectValue.value || null
  });

  React.useEffect(() => {
    setFilterField({
      field: initialSelectValue.field || null,
      condition:
        initialSelectValue.condition ||
        (conditionOptions && conditionOptions.length === 1
          ? (Object.values(conditionOptions[0])[0] as string)
          : null),
      value: initialSelectValue.value || null
    });
  }, [initialSelectValue, conditionOptions]);

  const updateFieldValue = (name: string, value: string) => {
    if (settingId) {
      const updated = { ...changedFields };
      const updatedFields = [...(updated[settingId] || [])];
      updatedFields[index] = {
        ...updatedFields[index],
        [name]: true
      } as FieldValidation;
      updated[settingId] = updatedFields;
      setChangedFields(updated);
    }

    const updatedField = {
      field: name === 'field' ? value : filterField.field,
      condition: name === 'condition' ? value : filterField.condition,
      value: name === 'value' ? value : filterField.value
    };

    setFilterField(updatedField);

    setSettings((prev: Setting[]) => {
      const newSettings = [...prev];
      newSettings[index] = {
        ...newSettings[index],
        field: updatedField.field,
        condition: updatedField.condition,
        value: updatedField.value
      };
      return newSettings;
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name as string;
    const value = e.target.value as string;
    updateFieldValue(name, value);
  };

  const handleSelectChange = (e: SelectChangeEvent<string>) => {
    const name = e.target.name as string;
    const value = e.target.value as string;
    updateFieldValue(name, value);
  };

  return (
    <div className={classes.fieldContainer}>
      {fieldOptions && fieldOptions.length > 0 ? (
        <FormControl size="small" className={classes.formControl}>
          <InputLabel id={`field-label-${index}`}>
            {formatMessage({ id: 'filterFieldSection.field' })}
          </InputLabel>
          <Select
            labelId={`field-label-${index}`}
            name="field"
            value={filterField.field || ''}
            onChange={handleSelectChange}
            label={formatMessage({ id: 'filterFieldSection.field' })}
            error={
              isFieldChanged.field
                ? !filterField.field || filterField.field.trim() === ''
                : invalidField.field
            }
          >
            <MenuItem value="" disabled>
              {inputSetting?.placeholder?.field ||
                formatMessage({ id: 'filterFieldSection.selectField' })}
            </MenuItem>
            {fieldOptions?.map((option: DropdownOption, idx: number) => {
              const key = Object.keys(option)[0];
              const value = Object.values(option)[0] as string;
              return (
                <MenuItem key={idx} value={value}>
                  {key}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      ) : (
        <TextField
          size="small"
          name="field"
          label={formatMessage({ id: 'filterFieldSection.field' })}
          value={filterField.field || ''}
          onChange={handleInputChange}
          placeholder={
            inputSetting?.placeholder?.field ||
            formatMessage({ id: 'filterFieldSection.enterFieldName' })
          }
          error={
            isFieldChanged.field
              ? !filterField.field || filterField.field.trim() === ''
              : invalidField.field
          }
          className={classes.formControl}
          InputProps={{
            classes: { input: classes.inputText }
          }}
        />
      )}

      <FormControl size="small" className={classes.conditionFormControl}>
        <InputLabel id={`condition-label-${index}`}>
          {formatMessage({ id: 'filterFieldSection.condition' })}
        </InputLabel>
        <Select
          labelId={`condition-label-${index}`}
          name="condition"
          value={filterField.condition || ''}
          onChange={handleSelectChange}
          label={formatMessage({ id: 'filterFieldSection.condition' })}
          error={
            isFieldChanged.condition
              ? !filterField.condition || filterField.condition.trim() === ''
              : invalidField.condition
          }
        >
          <MenuItem value="" disabled>
            {inputSetting?.placeholder?.condition ||
              formatMessage({ id: 'filterFieldSection.selectCondition' })}
          </MenuItem>
          {conditionOptions?.map((option: DropdownOption, idx: number) => {
            const key = Object.keys(option)[0];
            const value = Object.values(option)[0] as string;
            return (
              <MenuItem key={idx} value={value}>
                {key}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>

      <TextField
        size="small"
        name="value"
        label={formatMessage({ id: 'filterFieldSection.value' })}
        value={filterField.value || ''}
        onChange={handleInputChange}
        error={
          isFieldChanged.value
            ? !filterField.value || filterField.value.trim() === ''
            : invalidField.value
        }
        className={classes.valueFormControl}
        InputProps={{
          classes: { input: classes.inputText }
        }}
      />
    </div>
  );
};
